import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { Contact } from "@shared/schema";

interface ContactSelectProps {
  value?: string | null;
  onValueChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  clientGroupId?: string | null;
  /** Contact IDs to exclude from the dropdown (e.g., already-added contacts) */
  excludeContactIds?: string[];
  "data-testid"?: string;
}

export function ContactSelect({
  value,
  onValueChange,
  placeholder = "Select contact",
  disabled = false,
  clientGroupId,
  excludeContactIds = [],
  "data-testid": testId,
}: ContactSelectProps) {
  const { data: contacts, isLoading } = useQuery<Contact[]>({
    queryKey: clientGroupId
      ? ["/api/crm/contacts", { clientGroupId }]
      : ["/api/crm/contacts"],
  });

  const { data: selectedContact } = useQuery<Contact>({
    queryKey: ["/api/crm/contacts", value],
    enabled: !!value && !contacts?.some(contact => contact.id === value),
  });
  const retainedContact = selectedContact?.id === value ? selectedContact : undefined;

  // Filter out excluded contacts (ISSUE-162: prevent duplicate contact assignments)
  const availableContacts = useMemo(() => {
    if (!contacts) return [];
    if (excludeContactIds.length === 0) return contacts.filter(contact => !contact.archivedAt);

    const excludeSet = new Set(excludeContactIds);
    return contacts.filter((contact) => !excludeSet.has(contact.id) && !contact.archivedAt);
  }, [contacts, excludeContactIds]);

  return (
    <Select
      value={value || undefined}
      onValueChange={onValueChange}
      disabled={disabled || isLoading}
    >
      <SelectTrigger data-testid={testId}>
        <SelectValue placeholder={isLoading ? "Loading..." : placeholder}>
          {retainedContact ? `${retainedContact.firstName} ${retainedContact.lastName}` : undefined}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {retainedContact && !availableContacts.some(contact => contact.id === value) && (
          <SelectItem value={retainedContact.id} disabled>
            {retainedContact.firstName} {retainedContact.lastName}{retainedContact.archivedAt ? " (Archived)" : ""}
          </SelectItem>
        )}
        {availableContacts.length === 0 && !isLoading ? (
          <div className="py-2 px-2 text-sm text-muted-foreground text-center">
            No available contacts
          </div>
        ) : (
          availableContacts.map((contact) => (
            <SelectItem key={contact.id} value={contact.id}>
              {contact.firstName} {contact.lastName}
            </SelectItem>
          ))
        )}
      </SelectContent>
    </Select>
  );
}
